import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { EventedModal, ModalNames } from '|/app/modals';
import { CancelOrderModal } from '|/app/navigation-rail/components/modals';
import LinkButton from '|/components/carvana-ds/link-button';
import CarvanaCareModal from '|/components/carvana-ds/order-summary-display/carvana-care-modal';
import GapCoverageModal from '|/components/carvana-ds/order-summary-display/gap-coverage-modal';
import { SupportMfeModal } from '|/components/support-contact';
import {
  getCarvanaCareModalDeltas,
  getCarvanaCareModalVariants,
  getGapModalProps,
  getIsCarvanaFinanced
} from '|/helpers/carvana-care-modal-variants';
import { isCarvanaNativeApp, isAndroidNativeWebview } from '|/helpers/is-native-app';
import { MobileWebviewEvents } from '|/helpers/mobile-webview-events/mobile-webview-events';
import sendNativeEvent from '|/helpers/send-native-event';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import { useIsMobileViewport } from '|/hooks/use-is-mobile-viewport';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import HowYourePayingSection from './components/how-youre-paying-section';
import IncludedWithPurchaseSection from './components/included-with-purchase-section';
import TradeInDetailsModal from './components/trade-in-details-modal';
import TradeInSection from './components/trade-in-section';
import VehiclePriceSection from './components/vehicle-price-section';
import VehicleTile from './components/vehicle-tile';
import YourSelectionsSection from './components/your-selections-section';
import { derivePriceDetails } from './derive-price-details';
import derivePriceDetailsEventMetadata from './derive-price-details-event-metadata';
import styles from './price-details.module.css';

/**
 * This sheet is a depth-0 ds-modal, so it takes the default `1300 + 0 * 100`. Support opens
 * on top of it and defaults to 700, which renders it *behind* the sheet — the link looks
 * dead. 1400 is what a depth-1 nested modal would get, matching the CarvanaCare/GAP/cancel
 * modals that stack correctly because they render inside this modal's children.
 */
const SUPPORT_MODAL_Z_INDEX = 1400;

interface PriceDetailsProps {
  summary: ReviewSummaryResponse;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isCheckoutPage: boolean;
  /**
   * Unused here — the shared OrderSummary router spreads the same props into both arms and
   * the pre-check-it-out arm needs it. This sheet has no "Edit order" affordance; "Edit
   * details" navigates to CoF instead.
   */
  onEditOrder?: () => Promise<void>;
}

const PriceDetails = ({ summary, isOpen, setIsOpen, isCheckoutPage }: PriceDetailsProps) => {
  const [isCarvanaCareModalOpen, setIsCarvanaCareModalOpen] = useState(false);
  const [isGapModalOpen, setIsGapModalOpen] = useState(false);
  const [isTradeInDetailsOpen, setIsTradeInDetailsOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const { trackEvent } = useAnalyticsContext();
  const location = useLocation();
  const isMobileViewport = useIsMobileViewport();

  const view = derivePriceDetails(summary);

  const handleSupportClick = useCallback(() => {
    trackEvent('pp_header_support_contact_clicked');
    if (isCarvanaNativeApp()) {
      setIsOpen(false);
      sendNativeEvent({
        event: isAndroidNativeWebview()
          ? MobileWebviewEvents.OPEN_SEBASTIAN
          : MobileWebviewEvents.OPEN_SUPPORT,
        payload: { currentStep: location.pathname },
        source: 'purchase'
      });
      return;
    }
    // Deliberately does NOT close this sheet. `index.tsx` renders the whole order summary
    // behind `{isOrderSummaryModalOpen && ...}`, so closing it here unmounts this component
    // along with `isSupportModalOpen` and the `SupportMfeModal` below — the link then does
    // nothing at all. Support stacks on top instead (its own zIndex 700), like the
    // CarvanaCare/GAP/cancel modals.
    setIsSupportModalOpen(true);
  }, [trackEvent, setIsOpen, location.pathname]);

  const eventMetadata = derivePriceDetailsEventMetadata(view, summary?.customer?.address?.state);

  const { showSocialProof, impactsFinancingTerms } = getCarvanaCareModalVariants(summary);
  const { vehicleModel, monthlyDelta, downDelta, termDelta, hasLoanComparison } =
    getCarvanaCareModalDeltas(summary);
  const gapModalProps = getGapModalProps(summary);
  const isFinanced = getIsCarvanaFinanced(summary);

  // `drawerStyle` and `mobileStyle` are mutually exclusive in ds-modal's prop union
  // (drawer layout overrides mobile), so the two shapes are branched rather than merged.
  //
  // ds-modal 6.3 gives the sheet a slide-up animation and sizes it to its content. The sheet
  // has no height prop, so it is pinned to the bottom of the header via `classes.container`
  // — see `.sheetContainer`. The drag handle is hidden because the sheet is dismissed by its
  // close button, not by dragging.
  const modalStyleProps = isMobileViewport
    ? ({
        mobileStyle: 'sheet',
        showHandle: false,
        classes: { container: styles.sheetContainer }
      } as const)
    : ({ drawerStyle: 'all', drawerSide: 'right', drawerWidth: '415px' } as const);

  return (
    <>
      <EventedModal
        analyticsId={ModalNames.OrderSummary}
        aria-label="Price details"
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        {...modalStyleProps}
        eventMetadata={eventMetadata}
        contentPadding={false}
        disableOutsideClose={
          isCarvanaCareModalOpen ||
          isGapModalOpen ||
          isCancelModalOpen ||
          isSupportModalOpen ||
          isTradeInDetailsOpen
        }
        divLine={false}
      >
        <div className={styles.sheet} data-testid="price-details-treatment">
          <VehicleTile
            summary={summary}
            view={view}
            isCheckoutPage={isCheckoutPage}
            isMobileViewport={isMobileViewport}
            onSupportClick={handleSupportClick}
          />

          <h2 className={styles.sheetTitle}>Price details</h2>

          <div className={styles.breakdown}>
            <VehiclePriceSection summary={summary} view={view} />

            {view.showSelections && (
              <YourSelectionsSection
                summary={summary}
                view={view}
                onViewCarvanaCare={() => {
                  setIsCarvanaCareModalOpen(true);
                }}
                onViewGap={() => {
                  setIsGapModalOpen(true);
                }}
              />
            )}

            {view.showTradeIn && (
              <TradeInSection
                view={view}
                onViewTradeInDetails={() => {
                  setIsTradeInDetailsOpen(true);
                }}
              />
            )}
          </div>

          {view.showFinance && <HowYourePayingSection summary={summary} view={view} />}

          <IncludedWithPurchaseSection summary={summary} />

          {isCheckoutPage && (
            <div className={styles.cancelBlock}>
              <LinkButton
                size="sm"
                underline={false}
                className={styles.cancelSection}
                onClick={() => {
                  setIsCancelModalOpen(true);
                }}
                data-analytics-id="pp_order_summary_cancel_link"
              >
                <span className={styles.cancelTextRegular}>
                  Want to <span className={styles.cancelTextLink}>cancel your order?</span>
                </span>
              </LinkButton>
            </div>
          )}
        </div>

        <TradeInDetailsModal
          summary={summary}
          isOpen={isTradeInDetailsOpen}
          onClose={() => {
            setIsTradeInDetailsOpen(false);
          }}
        />

        <CarvanaCareModal
          isOpen={isCarvanaCareModalOpen}
          setIsOpen={setIsCarvanaCareModalOpen}
          coverageType={
            (summary?.product?.type as 'Essential' | 'Cosmetic' | 'Plus' | 'Premier') || 'Essential'
          }
          coverageDetails={{
            duration: `${summary?.product?.months} months`,
            mileage: `+${summary?.product?.mileage?.toLocaleString()} miles`
          }}
          fuelDescription={summary?.vehicle?.fuelDescription}
          showSocialProof={showSocialProof}
          impactsFinancingTerms={impactsFinancingTerms}
          variant="treatment"
          vehicleModel={vehicleModel}
          monthlyDelta={monthlyDelta}
          downDelta={downDelta}
          termDelta={termDelta}
          hasLoanComparison={hasLoanComparison}
          useSnapshotPricing
          fullPrice={summary?.product?.price ?? 0}
          isFinanced={isFinanced}
        />

        <GapCoverageModal
          isOpen={isGapModalOpen}
          onClose={() => {
            setIsGapModalOpen(false);
          }}
          impactsFinancingTerms={gapModalProps.impactsFinancingTerms}
          vehicleModel={gapModalProps.vehicleModel}
          termMonths={gapModalProps.termMonths}
          monthlyDelta={gapModalProps.monthlyDelta}
          downDelta={gapModalProps.downDelta}
          termDelta={gapModalProps.termDelta}
          hasLoanComparison={gapModalProps.hasLoanComparison}
          fullPrice={summary?.gap?.price ?? 0}
          isFinanced={isFinanced}
          useSnapshotPricing
        />

        {isCheckoutPage && (
          <CancelOrderModal
            isOpen={isCancelModalOpen}
            onClose={() => {
              setIsCancelModalOpen(false);
            }}
          />
        )}
      </EventedModal>
      <SupportMfeModal
        isOpen={isSupportModalOpen}
        onClose={() => {
          setIsSupportModalOpen(false);
        }}
        zIndex={SUPPORT_MODAL_Z_INDEX}
      />
    </>
  );
};

export default PriceDetails;
